import { fetchProductById, fetchProductVariantById } from "../api/CatalogAPIs";
import { API_BASE_URL_V2 } from "../api/URL_Header";

const NAME_SEPARATOR = " || ";
const META_PREFIX = "__catalogMeta:";

function positiveId(detail, ...keys) {
  if (!detail) return 0;
  for (const key of keys) {
    const value = Number(detail[key]);
    if (value > 0) return value;
  }
  return 0;
}

export function isReadyProductDetail(detail) {
  if (!detail) return false;
  const variantId = positiveId(detail, "productVariantId", "ProductVariantId");
  const productId = positiveId(detail, "productId", "ProductId");
  const designId = positiveId(detail, "designId", "DesignId");
  if (variantId > 0 && designId <= 0) return true;
  if (productId > 0 && designId <= 0) return true;
  const itemType = String(detail.itemType || detail.ItemType || "");
  return (itemType === "ReadyMade" || itemType === "SimpleReadyMade" || itemType === "2" || itemType === "3")
    && designId <= 0
    && (variantId > 0 || productId > 0);
}

function displayableImage(value) {
  if (!value || String(value).startsWith(META_PREFIX)) return "";
  const path = String(value);
  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("data:") ||
    path.startsWith("blob:")
  ) {
    return path;
  }
  return `${API_BASE_URL_V2}${path.startsWith("/") ? "" : "/"}${path}`;
}

function firstDisplayable(values) {
  for (const value of values || []) {
    const image = displayableImage(value);
    if (image) return image;
  }
  return "";
}

const readyCardCache = new Map();

async function describeReadyProduct(detail) {
  const variantId = positiveId(detail, "productVariantId", "ProductVariantId");
  const productId = positiveId(detail, "productId", "ProductId");
  const cacheKey = variantId ? `v:${variantId}` : `p:${productId}`;
  if (readyCardCache.has(cacheKey)) return readyCardCache.get(cacheKey);

  let card = {
    id: variantId || productId || positiveId(detail, "orderDetailsId", "OrderDetailsId"),
    name: variantId ? `منتج جاهز #${variantId}` : `منتج جاهز #${productId}`,
    type: "منتج جاهز",
    phoneType: "منتج جاهز",
    caseStyleName: "",
    driveLink: "",
    code: "READY",
    image: "",
    itemKind: "ready",
    productId: productId || null,
    productVariantId: variantId || null,
    designId: null,
    typeId: null,
  };

  try {
    if (variantId) {
      const variant = await fetchProductVariantById(variantId);
      const ownerId = positiveId(variant, "productId", "ProductId") || productId;
      let product = null;
      if (ownerId) {
        try { product = await fetchProductById(ownerId); } catch { product = null; }
      }
      const name = displayName(variant?.productName || variant?.ProductName || product?.name || product?.Name);
      const color = displayName(variant?.colorName || variant?.ColorName);
      const phone = displayName(variant?.typeName || variant?.TypeName);
      const subtitle = [phone, color].filter(Boolean).join(" · ") || variant?.code || variant?.Code || "منتج جاهز";
      card = {
        ...card,
        id: variantId,
        productId: ownerId || null,
        name: name || card.name,
        type: subtitle,
        phoneType: subtitle,
        code: variant?.code || variant?.Code || "READY",
        image: firstDisplayable([
          ...(variant?.imageUrls || variant?.ImageUrls || []),
          product?.imageUrl || product?.ImageUrl,
          ...(product?.imageUrls || product?.ImageUrls || []),
        ]),
      };
    } else if (productId) {
      const product = await fetchProductById(productId);
      card = {
        ...card,
        id: productId,
        name: displayName(product?.name || product?.Name) || card.name,
        type: "منتج جاهز",
        phoneType: "منتج جاهز",
        code: "READY",
        image: firstDisplayable([
          product?.imageUrl || product?.ImageUrl,
          ...(product?.imageUrls || product?.ImageUrls || []),
        ]),
      };
    }
  } catch (err) {
    console.error("Ready product line could not be loaded", err);
  }

  readyCardCache.set(cacheKey, card);
  return card;
}

export async function readyOrderUnits(detail) {
  if (!isReadyProductDetail(detail)) return null;
  const card = await describeReadyProduct(detail);
  const quantity = Math.max(Number(detail.quantity ?? detail.Quantity) || 0, 0);
  const names = {
    price: detail.price ?? detail.Price,
    customNameAr: detail.customNameAr || detail.CustomNameAr || "",
    customNameEn: detail.customNameEn || detail.CustomNameEn || "",
    hasPersonalizedName: detailHasPersonalizedName(detail),
    orderDetailsId: detail.orderDetailsId || detail.OrderDetailsId || null,
  };
  if (quantity === 0) return [];
  return Array.from({ length: quantity }, (_, index) => ({
    ...card,
    ...names,
    quantityIndex: index + 1,
    totalQuantity: quantity,
    quantity,
  }));
}

function displayName(value) {
  if (!value) return "";
  const text = String(value);
  const index = text.indexOf(NAME_SEPARATOR);
  if (index === -1) return text;
  return text.slice(0, index).trim() || text.slice(index + NAME_SEPARATOR.length).trim();
}

export function caseStyleNameFromDetail(detail) {
  const raw = detail?.caseStyle?.name || detail?.caseStyleName || "";
  return displayName(raw);
}

export function formatPhoneWithCaseStyle(typeName, detailOrStyleName) {
  const style =
    typeof detailOrStyleName === "string"
      ? displayName(detailOrStyleName)
      : caseStyleNameFromDetail(detailOrStyleName);
  const type = typeName || "";
  if (!style) return type;
  return type ? `${type} · ${style}` : style;
}

const STYLE_MARK = " · ";

export const CASE_STYLE_FILTER_ALL = "all";
export const CASE_STYLE_FILTER_NORMAL = "normal";

function styleText(name) {
  return displayName(name).trim().toLowerCase();
}

export function isTransparentCaseStyle(name) {
  const text = styleText(name);
  return text === "شفاف" || text.includes("transparent");
}

export function isBlackEdgeCaseStyle(name) {
  const text = styleText(name);
  return text.includes("حواف سوداء") || text.includes("black edge");
}

export function isSpecialCaseStyle(name) {
  const style = displayName(name).trim();
  if (!style) return false;
  return !isTransparentCaseStyle(style);
}

export function caseStyleFromItem(item) {
  if (!item) return "";
  if (item.caseStyleName) return displayName(item.caseStyleName);
  const type = String(item.type || "");
  const index = type.lastIndexOf(STYLE_MARK);
  if (index === -1) return "";
  return type.slice(index + STYLE_MARK.length).trim();
}

export function phoneTypeFromItem(item) {
  if (!item) return "";
  if (item.phoneType) return item.phoneType;
  const type = String(item.type || "");
  const style = caseStyleFromItem(item);
  const suffix = style ? `${STYLE_MARK}${style}` : "";
  if (suffix && type.endsWith(suffix)) return type.slice(0, -suffix.length);
  return type;
}

export function orderCaseStyles(order) {
  const names = [];
  const seen = new Set();
  for (const item of order?.items || []) {
    const style = caseStyleFromItem(item);
    if (!style || seen.has(style)) continue;
    seen.add(style);
    names.push(style);
  }
  return names;
}

export function orderHasBlackEdges(order) {
  return (order?.items || []).some((item) => isBlackEdgeCaseStyle(caseStyleFromItem(item)));
}

export function detailHasPersonalizedName(detail) {
  if (!detail) return false;
  if (detail.hasPersonalizedName === true || detail.HasPersonalizedName === true) return true;
  const arabic = detail.customNameAr ?? detail.CustomNameAr ?? "";
  const english = detail.customNameEn ?? detail.CustomNameEn ?? "";
  return Boolean(String(arabic).trim() || String(english).trim());
}

export function orderHasPersonalizedName(order) {
  return (order?.items || []).some((item) => item?.hasPersonalizedName);
}

export function namedOrderRowClass(order) {
  if (orderHasBlackEdges(order)) {
    return "border-b border-zinc-300 bg-zinc-100 hover:bg-zinc-200/80 transition-colors shadow-[inset_4px_0_0_0_#52525b]";
  }
  if (!orderHasPersonalizedName(order)) {
    return "border-b border-gray-200 hover:bg-white/60 transition-colors";
  }
  return "border-b border-fuchsia-300 bg-fuchsia-50 hover:bg-fuchsia-100/80 transition-colors shadow-[inset_4px_0_0_0_#c026d3]";
}

export function namedItemBoxClass(item) {
  if (isBlackEdgeCaseStyle(caseStyleFromItem(item))) {
    return "mb-3 rounded-lg border-2 border-zinc-400 bg-zinc-100 p-2";
  }
  if (!item?.hasPersonalizedName) return "mb-3";
  return "mb-3 rounded-lg border-2 border-fuchsia-400 bg-fuchsia-50/80 p-2";
}

export function orderMatchesCaseStyleFilter(order, filter) {
  if (!filter || filter === CASE_STYLE_FILTER_ALL) return true;
  const specials = orderCaseStyles(order).filter(isSpecialCaseStyle);
  if (filter === CASE_STYLE_FILTER_NORMAL) return specials.length === 0;
  return specials.includes(filter);
}

export function specialCaseStyleLabels(catalogStyles, orders) {
  const labels = [];
  const seen = new Set();
  const add = (raw) => {
    const name = displayName(typeof raw === "string" ? raw : raw?.name || "");
    if (!isSpecialCaseStyle(name) || seen.has(name)) return;
    seen.add(name);
    labels.push(name);
  };
  for (const style of catalogStyles || []) add(style);
  for (const order of orders || []) {
    for (const name of orderCaseStyles(order)) add(name);
  }
  return labels;
}

export function teePlusPackedItemData(item) {
  const style = caseStyleFromItem(item);
  const phoneType = phoneTypeFromItem(item) || "Unknown Type";
  const type = isBlackEdgeCaseStyle(style) ? `Magic ${phoneType}` : phoneType;
  const driveLink = item?.driveLink || "No Link";
  const product = isBlackEdgeCaseStyle(style)
    ? "Magic Cover (H) + Packing"
    : "Phone Cover (H) + Packing";
  return `\t\t${product}\t${type}\t\t1\t${driveLink}`;
}
